import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Sparkles, ChevronRight, ChevronLeft, X, Info } from "lucide-react";

const STORAGE_KEY = "productSaleUnitsTourSeen_v1";

/** Ejemplos de productos ferreteros comunes con recomendaciones de sub-unidades */
export const SALE_UNIT_SUGGESTIONS = [
  { keywords: ["cemento", "titan", "cibao"],
    base: "SACO", suggested: [{ unit: "LB", ratio: 94 }, { unit: "KG", ratio: 42.5 }],
    note: "Cemento portland de 94 lb / 42.5 kg — se vende suelto por libra a albañiles." },
  { keywords: ["tubo 3/4", "tubo 1/2", "tubo 1\"", "tubo 2\"", "presión", "pvc"],
    base: "TUBO", suggested: [{ unit: "PIE", ratio: 19 }],
    note: "Tubería PVC de 19 pies — vender por pie cuando se necesitan cortes." },
  { keywords: ["clavo", "tornillo"],
    base: "LB", suggested: [{ unit: "1/4 LB", ratio: 4 }],
    note: "Clavos/tornillos por 1/4 de libra para reparaciones pequeñas." },
  { keywords: ["varilla", "cabilla", "acero"],
    base: "VAR", suggested: [{ unit: "PIE", ratio: 20 }],
    note: "Varilla estándar de 20 pies — vender por pie cortado." },
  { keywords: ["cable", "alambre", "electrico", "eléctrico"],
    base: "ROLLO", suggested: [{ unit: "MT", ratio: 100 }, { unit: "PIE", ratio: 328 }],
    note: "Cable eléctrico por rollo de 100 m — vender por metro o pie." },
  { keywords: ["pintura", "galón", "galon"],
    base: "GAL", suggested: [{ unit: "CUARTO", ratio: 4 }],
    note: "Pintura por galón — vender por cuarto para trabajos menores." },
  { keywords: ["madera", "tabla", "plywood"],
    base: "PLANCHA", suggested: [{ unit: "PIE2", ratio: 32 }],
    note: "Plywood 4×8 = 32 pies². Vender por pie cuadrado para muebleros." },
];

/**
 * Sugiere sub-unidades para un producto basado en su nombre.
 * Devuelve la primera coincidencia o null.
 */
export function suggestForProduct(name = "") {
  const n = name.toLowerCase();
  return SALE_UNIT_SUGGESTIONS.find((s) => s.keywords.some((k) => n.includes(k))) || null;
}

const STEPS = [
  {
    title: "¿Cuándo usar unidades alternas?",
    icon: Info,
    body: (
      <div className="space-y-2 text-sm">
        <p>Configura <b>unidades alternas de venta</b> cuando el mismo producto se pueda vender en dos o más presentaciones sin duplicar tu inventario.</p>
        <div className="bg-muted/40 p-2 rounded text-xs">
          <b>Ejemplo clásico:</b> compras un saco de cemento (94 lb) y quieres venderlo tanto entero como suelto por libra al albañil que solo necesita 10 lb.
        </div>
        <p className="text-xs text-muted-foreground">Ventaja: un solo SKU, un solo stock, y el sistema decrementa el saco automáticamente al vender libras (10 lb = 10/94 = 0.1064 sacos).</p>
      </div>
    ),
  },
  {
    title: "¿Qué es el `ratio`?",
    icon: Sparkles,
    body: (
      <div className="space-y-2 text-sm">
        <p><b>Ratio</b> = cuántas sub-unidades caben en 1 unidad base.</p>
        <table className="w-full text-xs border border-border rounded overflow-hidden">
          <thead className="bg-muted/40">
            <tr><th className="p-2 text-left">Producto base</th><th className="p-2 text-left">Sub-unidad</th><th className="p-2 text-right">Ratio</th></tr>
          </thead>
          <tbody>
            <tr className="border-t border-border/40"><td className="p-2">Saco de cemento</td><td className="p-2">Libra (LB)</td><td className="p-2 text-right font-mono">94</td></tr>
            <tr className="border-t border-border/40"><td className="p-2">Tubo PVC 3/4&quot;</td><td className="p-2">Pie (PIE)</td><td className="p-2 text-right font-mono">19</td></tr>
            <tr className="border-t border-border/40"><td className="p-2">Galón de pintura</td><td className="p-2">Cuarto</td><td className="p-2 text-right font-mono">4</td></tr>
            <tr className="border-t border-border/40"><td className="p-2">1 libra de clavos</td><td className="p-2">1/4 libra</td><td className="p-2 text-right font-mono">4</td></tr>
            <tr className="border-t border-border/40"><td className="p-2">Rollo de cable 100 m</td><td className="p-2">Metro</td><td className="p-2 text-right font-mono">100</td></tr>
          </tbody>
        </table>
      </div>
    ),
  },
  {
    title: "Costo y margen automáticos",
    icon: Sparkles,
    body: (
      <div className="space-y-2 text-sm">
        <p>El sistema calcula el <b>costo por sub-unidad</b> automáticamente:</p>
        <div className="bg-muted/40 p-2 rounded text-xs font-mono">
          costo_sub_unidad = costo_producto ÷ ratio
        </div>
        <p className="text-xs">Ejemplo: saco de cemento cuesta RD$400 y tiene ratio 94 &rarr; costo por libra = <b>RD$4.26</b>. Si vendes la libra a RD$6.50, obtienes un margen del <span className="text-success font-bold">34.5%</span>.</p>
        <p className="text-xs text-muted-foreground">La columna &quot;Costo/margen&quot; en la tabla de unidades te muestra esto en vivo mientras editas.</p>
      </div>
    ),
  },
  {
    title: "Buenas prácticas",
    icon: Info,
    body: (
      <div className="space-y-2 text-sm">
        <ul className="list-disc list-inside text-sm space-y-1">
          <li>Activa <b>Permitir venta fraccional</b> antes de agregar sub-unidades (se hace automáticamente al añadir la primera).</li>
          <li>Usa códigos <b>cortos y estándar</b> para la sub-unidad: LB, KG, PIE, MT, CUARTO.</li>
          <li>Precia la sub-unidad ligeramente por encima del prorrateo (ej. RD$611 en libras vs RD$500 el saco entero) — es común cobrar más al detalle.</li>
          <li>Si el producto tiene <b>merma</b> significativa (ej. cemento se derrama al servir), sube un poco el precio por libra.</li>
          <li>Los reportes en &quot;Reportes &rarr; Ventas por Unidad&quot; muestran cuánto vendes de cada presentación.</li>
        </ul>
      </div>
    ),
  },
];

export default function SaleUnitsTour({ open, onClose }) {
  const [step, setStep] = useState(0);
  useEffect(() => { if (open) setStep(0); }, [open]);

  const finish = () => {
    try { localStorage.setItem(STORAGE_KEY, "1"); } catch (e) { /* ignore */ }
    onClose?.();
  };

  if (!open) return null;
  const S = STEPS[step];
  const Icon = S.icon;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) finish(); }}>
      <DialogContent className="max-w-lg" data-testid="sale-units-tour">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Icon className="w-5 h-5 text-primary" />
            <span>Guía: Unidades alternas de venta</span>
          </DialogTitle>
        </DialogHeader>
        <div className="mb-1 text-xs text-muted-foreground">
          Paso {step + 1} de {STEPS.length} · <b>{S.title}</b>
        </div>
        <div className="min-h-[180px]">{S.body}</div>
        <div className="flex items-center justify-between mt-2 pt-2 border-t border-border/60">
          <Button variant="ghost" size="sm" onClick={finish} data-testid="tour-skip">
            Omitir
          </Button>
          <div className="flex gap-1">
            {STEPS.map((_, i) => (
              <span key={i} className={`w-1.5 h-1.5 rounded-full ${i === step ? "bg-primary" : "bg-muted"}`} />
            ))}
          </div>
          <div className="flex gap-2">
            {step > 0 && (
              <Button variant="outline" size="sm" onClick={() => setStep((s) => s - 1)} data-testid="tour-prev">
                <ChevronLeft className="w-4 h-4" />
              </Button>
            )}
            {step < STEPS.length - 1 && (
              <Button size="sm" onClick={() => setStep((s) => s + 1)} data-testid="tour-next">
                Siguiente <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            )}
            {step === STEPS.length - 1 && (
              <Button size="sm" onClick={finish} data-testid="tour-done">
                Entendido
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/** Hook para saber si el tour ya se ha visto. */
export function useTourSeen() {
  const [seen, setSeen] = useState(() => {
    try { return !!localStorage.getItem(STORAGE_KEY); } catch (e) { return false; }
  });
  const reset = () => {
    try { localStorage.removeItem(STORAGE_KEY); } catch (e) { /* ignore */ }
    setSeen(false);
  };
  return { seen, reset };
}
